import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';

import { FtpServerType, FtpServerTypeUtil, FtpService } from 'src/app/domainObjects/dataConnector';
import { OrganizationService } from 'src/app/views/organization/organization.service';
import { OrgFtpService, toFtpApiError } from 'src/app/views/settings/organization-settings/ftp-setup/org-ftp-service.model';

export type FtpConnectionStatus = 'none' | 'testing' | 'success' | 'failed';

/**
 * Why an FTP-backed connector action can't proceed yet, or null when it can.
 * Shared by the ingest connectors so they all apply the same rule: with Use FTP
 * on, nothing proceeds until a selected service has passed its connection test.
 */
export function ftpNotReadyMessage(isUseFtp: boolean, status: FtpConnectionStatus): string | null {
  if (!isUseFtp || status === 'success') {
    return null;
  }
  switch (status) {
    case 'testing': return 'Please wait for the FTP connection test to finish.';
    case 'failed': return 'The selected FTP service could not be reached. Retry the connection test or select another FTP service.';
    default: return 'Select an FTP service before continuing.';
  }
}

interface Settled<T> {
  ok: boolean;
  value?: T;
  error?: unknown;
}

function settle<T>(promise: Promise<T>): Promise<Settled<T>> {
  return promise.then(value => ({ ok: true, value }), error => ({ ok: false, error }));
}

/** True when Use FTP is on but no FTP service has populated the connector's FTP settings. */
export function isFtpServiceMissing(isUseFtp: boolean, ftpService: FtpService | undefined): boolean {
  return !!isUseFtp && !ftpService?.ftpServerLocation;
}

/**
 * FTP service selection. The Analyst picks one of the organization's FTP services
 * (Organization Settings → FTP) and its connection is tested automatically.
 *
 * Two modes:
 * - Configuration mode (default; ingest connectors): the selected service's full
 *   configuration is loaded into the bound FtpService model (`value`), and a saved
 *   model is matched back to its service on reopen.
 * - Id mode (`loadConfiguration` false; Data Writer): only the service id is bound
 *   (`selectedServiceId`) — no configuration, and so no password, is fetched; the
 *   engine resolves the id itself at run time.
 */
@Component({
  selector: 'app-ftp-service-select',
  templateUrl: './ftp-service-select.component.html',
  styleUrls: ['./ftp-service-select.component.css'],
  standalone: false
})
export class FtpServiceSelectComponent implements OnInit, OnChanges {
  /** The connector's current FTP settings (used to reselect a saved service). */
  @Input() value?: FtpService;
  @Output() valueChange = new EventEmitter<FtpService>();
  @Output() connectionStatusChange = new EventEmitter<FtpConnectionStatus>();

  /** False = id mode: select and test only, never fetch the configuration. */
  @Input() loadConfiguration = true;
  /** Id mode: the saved service id to restore (null/undefined = none selected). */
  @Input() selectedServiceId?: number | null;
  @Output() selectedServiceIdChange = new EventEmitter<number | null>();

  readonly control = new FormControl<number | null>(null, Validators.required);
  readonly FtpServerTypeUtil = FtpServerTypeUtil;

  services: OrgFtpService[] = [];
  loadingList = false;
  listError = '';
  /** Set when a saved configuration matches none of the organization's services. */
  unmatchedHost = '';
  /** Id mode: the saved service id no longer exists (or is inactive) in this organization. */
  savedServiceMissing = false;
  status: FtpConnectionStatus = 'none';
  statusMessage = '';

  private lastEmitted?: FtpService;
  private lastEmittedId?: number | null;
  private listLoaded = false;
  // Incremented per selection so a slow response for a previous selection can
  // never overwrite the state of the current one.
  private selectionSeq = 0;

  constructor(private organizationService: OrganizationService) {}

  /** The currently selected service (name and server type shown with its connection status). */
  get selectedService(): OrgFtpService | undefined {
    const id = this.control.value;
    return id == null ? undefined : this.services.find(s => s.id === id);
  }

  isSftp(service: OrgFtpService): boolean {
    return Number(service.ftpServerType) === FtpServerType.SFTP;
  }

  ngOnInit(): void {
    this.loadServices();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // A different connector configuration was bound (not our own emitted value).
    if (changes['value'] && !changes['value'].firstChange && this.value !== this.lastEmitted && this.listLoaded) {
      this.selectSavedService();
    }
    // Id mode: a different saved selection was bound (e.g. another writer type).
    if (changes['selectedServiceId'] && !changes['selectedServiceId'].firstChange &&
        this.selectedServiceId !== this.lastEmittedId && this.listLoaded) {
      this.selectSavedService();
    }
  }

  async loadServices(): Promise<void> {
    this.loadingList = true;
    this.listError = '';
    this.control.disable();
    try {
      const all = await this.organizationService.getOrgFtpServices() ?? [];
      this.services = all.filter(s => s.isActive === 1);
      this.listLoaded = true;
      if (this.services.length > 0) {
        this.control.enable();
      }
      this.selectSavedService();
    } catch (err) {
      this.listError = toFtpApiError(err).message;
    } finally {
      this.loadingList = false;
    }
  }

  onSelectionChange(id: number): void {
    this.unmatchedHost = '';
    this.savedServiceMissing = false;
    this.emitSelectedId(id);
    this.loadAndTest(id);
  }

  retry(): void {
    if (this.control.value != null) {
      this.loadAndTest(this.control.value);
    }
  }

  /**
   * Loads the selected service's configuration into the connector model and tests
   * it. The previous configuration is cleared immediately so it can never be used
   * while the new one is loading.
   */
  private async loadAndTest(id: number): Promise<void> {
    const seq = ++this.selectionSeq;
    if (this.loadConfiguration) {
      this.emitValue(new FtpService(FtpServerType.SFTP, '', '', '', 22));
    }
    this.setStatus('testing', 'Testing connection…');

    // Load and test run in parallel; each settles independently so one failing
    // doesn't hide the other's result.
    const [connection, test] = await Promise.all([
      this.loadConfiguration
        ? settle(this.organizationService.getOrgFtpServiceConnection(id))
        : Promise.resolve<Settled<FtpService>>({ ok: true }),
      settle(this.organizationService.testOrgFtpService(id))
    ]);
    if (seq !== this.selectionSeq) {
      return;
    }

    if (!connection.ok) {
      this.setStatus('failed', toFtpApiError(connection.error).message);
      return;
    }
    if (this.loadConfiguration) {
      this.emitValue(connection.value);
    }

    if (!test.ok) {
      this.setStatus('failed', toFtpApiError(test.error).message);
    } else if (test.value?.success) {
      this.setStatus('success', 'FTP connection successful');
    } else {
      this.setStatus('failed', test.value?.message || 'FTP connection failed');
    }
  }

  /**
   * Reselects the service matching the connector's saved settings (server type,
   * host, port and username) and retests it with the current stored configuration.
   */
  private selectSavedService(): void {
    this.unmatchedHost = '';
    this.savedServiceMissing = false;
    if (!this.loadConfiguration) {
      this.selectSavedServiceById();
      return;
    }
    const saved = this.value;
    if (!saved?.ftpServerLocation) {
      this.control.setValue(null);
      this.setStatus('none', '');
      return;
    }
    const match = this.services.find(s =>
      Number(s.ftpServerType) === Number(saved.ftpServerType) &&
      s.ftpServerLocation.toLowerCase() === saved.ftpServerLocation.trim().toLowerCase() &&
      Number(s.controlPort) === Number(saved.controlPort) &&
      s.userName === saved.userName?.trim());

    if (match) {
      this.control.setValue(match.id);
      this.loadAndTest(match.id);
    } else {
      // Leave the saved settings untouched (existing configs keep working), but
      // require an explicit choice before anything FTP-backed can proceed.
      this.control.setValue(null);
      this.unmatchedHost = saved.ftpServerLocation;
      this.setStatus('none', '');
    }
  }

  /**
   * Id mode: reselects and retests the saved service id. If it no longer exists or
   * is inactive, the selection is cleared so a new service must be chosen.
   */
  private selectSavedServiceById(): void {
    const savedId = this.selectedServiceId;
    if (savedId == null) {
      this.control.setValue(null);
      this.setStatus('none', '');
      return;
    }
    if (this.services.some(s => s.id === savedId)) {
      this.control.setValue(savedId);
      this.lastEmittedId = savedId;
      this.loadAndTest(savedId);
    } else {
      this.control.setValue(null);
      this.savedServiceMissing = true;
      this.emitSelectedId(null);
      this.setStatus('none', '');
    }
  }

  private emitSelectedId(id: number | null): void {
    this.lastEmittedId = id;
    this.selectedServiceIdChange.emit(id);
  }

  private emitValue(value: FtpService): void {
    this.lastEmitted = value;
    this.valueChange.emit(value);
  }

  private setStatus(status: FtpConnectionStatus, message: string): void {
    this.status = status;
    this.statusMessage = message;
    this.connectionStatusChange.emit(status);
  }
}
